/**
 * Admin Roadmap Service
 * CRUD operations for expert-made roadmaps created by admin users
 * 
 * Functions:
 * - createAdminRoadmap: Create new expert roadmap with days
 * - updateAdminRoadmap: Update roadmap metadata
 * - updateAdminRoadmapDay: Update specific day content
 * - deleteAdminRoadmap: Delete roadmap and all associated days
 * - getAdminRoadmaps: Get all published admin roadmaps (or draft if admin)
 * - getAdminRoadmapById: Get specific roadmap with all days
 * - getUserProgressOnAdminRoadmap: Get user's progress
 * - updateUserProgressOnAdminRoadmap: Update user progress
 */

import { supabase } from '@/config/supabase';
import type { User } from '@supabase/supabase-js';

export interface AdminRoadmapDay {
  id?: string;
  day_number: number;
  title: string;
  subtitle?: string;
  description?: string;
  video_id?: string;
  video_url?: string;
  resources?: {
    links: Array<{
      title: string;
      url: string;
      type?: string;
    }>;
  };
  learning_objectives?: string[];
  practice_exercises?: any[];
}

export interface AdminRoadmapData {
  id?: string;
  title: string;
  description?: string;
  total_days: number;
  roadmap_type: 'Skill' | 'Academic' | 'Religious';
  difficulty?: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
  tags?: string[];
  is_published?: boolean;
  days?: AdminRoadmapDay[];
}

export interface AdminRoadmapWithDays extends AdminRoadmapData {
  created_by?: string;
  view_count?: number;
  created_at?: string;
  updated_at?: string;
  days?: AdminRoadmapDay[];
}

/**
 * Create new admin roadmap with days
 */
export async function createAdminRoadmap(
  user: User,
  roadmapData: AdminRoadmapData
): Promise<{ data: AdminRoadmapWithDays | null; error: any }> {
  try {
    // Step 1: Create roadmap
    const { data: roadmapRecord, error: roadmapError } = await supabase
      .from('admin_roadmaps')
      .insert({
        title: roadmapData.title,
        description: roadmapData.description || '',
        total_days: roadmapData.total_days,
        roadmap_type: roadmapData.roadmap_type,
        difficulty: roadmapData.difficulty || 'Beginner',
        created_by: user.id,
        is_published: roadmapData.is_published || false,
        tags: roadmapData.tags || [],
        meta: {
          created_by_email: user.email,
          created_by_name: user.user_metadata?.name || 'Admin User',
        },
      })
      .select()
      .single();

    if (roadmapError) {
      console.error('Error creating roadmap:', roadmapError);
      return { data: null, error: roadmapError };
    }

    // Step 2: Create days if provided
    if (roadmapData.days && roadmapData.days.length > 0) {
      const daysToInsert = roadmapData.days.map((day) => ({
        admin_roadmap_id: roadmapRecord.id,
        day_number: day.day_number,
        title: day.title,
        subtitle: day.subtitle || '',
        description: day.description || '',
        video_id: day.video_id || '',
        video_url: day.video_url || '',
        resources: day.resources || { links: [] },
        learning_objectives: day.learning_objectives || [],
        practice_exercises: day.practice_exercises || [],
        order_index: day.day_number,
      }));

      const { error: daysError } = await supabase
        .from('admin_roadmap_days')
        .insert(daysToInsert);

      if (daysError) {
        console.error('Error creating roadmap days:', daysError);
        // Roadmap created but days failed - still return roadmap
        return { data: roadmapRecord, error: daysError };
      }
    }

    return { data: roadmapRecord as AdminRoadmapWithDays, error: null };
  } catch (error) {
    console.error('Error in createAdminRoadmap:', error);
    return { data: null, error };
  }
}

/**
 * Get all admin roadmaps (published for users, all for admin)
 */
export async function getAdminRoadmaps(
  userIsAdmin?: boolean
): Promise<{ data: AdminRoadmapWithDays[]; error: any }> {
  try {
    let query = supabase
      .from('admin_roadmaps')
      .select('*');

    if (!userIsAdmin) {
      query = query.eq('is_published', true);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching admin roadmaps:', error);
      return { data: [], error };
    }

    // Fetch days for each roadmap
    const roadsWithDays = await Promise.all(
      (data || []).map(async (roadmap) => {
        const { data: days, error: daysError } = await supabase
          .from('admin_roadmap_days')
          .select('*')
          .eq('admin_roadmap_id', roadmap.id)
          .order('day_number', { ascending: true });

        return {
          ...roadmap,
          days: daysError ? [] : days || [],
        };
      })
    );

    return { data: roadsWithDays as AdminRoadmapWithDays[], error: null };
  } catch (error) {
    console.error('Error in getAdminRoadmaps:', error);
    return { data: [], error };
  }
}

/**
 * Get specific admin roadmap by ID with all days
 */
export async function getAdminRoadmapById(
  roadmapId: string
): Promise<{ data: AdminRoadmapWithDays | null; error: any }> {
  try {
    const { data: roadmap, error: roadmapError } = await supabase
      .from('admin_roadmaps')
      .select('*')
      .eq('id', roadmapId)
      .single();

    if (roadmapError) {
      console.error('Error fetching roadmap:', roadmapError);
      return { data: null, error: roadmapError };
    }

    const { data: days, error: daysError } = await supabase
      .from('admin_roadmap_days')
      .select('*')
      .eq('admin_roadmap_id', roadmapId)
      .order('day_number', { ascending: true });

    return {
      data: {
        ...roadmap,
        days: daysError ? [] : days || [],
      } as AdminRoadmapWithDays,
      error: null,
    };
  } catch (error) {
    console.error('Error in getAdminRoadmapById:', error);
    return { data: null, error };
  }
}

/**
 * Update admin roadmap metadata
 */
export async function updateAdminRoadmap(
  roadmapId: string,
  updates: Partial<AdminRoadmapData>
): Promise<{ data: AdminRoadmapWithDays | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('admin_roadmaps')
      .update({
        title: updates.title,
        description: updates.description,
        total_days: updates.total_days,
        roadmap_type: updates.roadmap_type,
        difficulty: updates.difficulty,
        is_published: updates.is_published,
        tags: updates.tags,
        updated_at: new Date().toISOString(),
      })
      .eq('id', roadmapId)
      .select()
      .single();

    if (error) {
      console.error('Error updating roadmap:', error);
      return { data: null, error };
    }

    // Fetch updated days
    const { data: days } = await supabase
      .from('admin_roadmap_days')
      .select('*')
      .eq('admin_roadmap_id', roadmapId)
      .order('day_number', { ascending: true });

    return {
      data: {
        ...data,
        days: days || [],
      } as AdminRoadmapWithDays,
      error: null,
    };
  } catch (error) {
    console.error('Error in updateAdminRoadmap:', error);
    return { data: null, error };
  }
}

/**
 * Update specific day in admin roadmap
 */
export async function updateAdminRoadmapDay(
  dayId: string,
  updates: Partial<AdminRoadmapDay>
): Promise<{ data: AdminRoadmapDay | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('admin_roadmap_days')
      .update({
        title: updates.title,
        subtitle: updates.subtitle,
        description: updates.description,
        video_id: updates.video_id,
        video_url: updates.video_url,
        resources: updates.resources,
        learning_objectives: updates.learning_objectives,
        practice_exercises: updates.practice_exercises,
        updated_at: new Date().toISOString(),
      })
      .eq('id', dayId)
      .select()
      .single();

    if (error) {
      console.error('Error updating roadmap day:', error);
      return { data: null, error };
    }

    return { data: data as AdminRoadmapDay, error: null };
  } catch (error) {
    console.error('Error in updateAdminRoadmapDay:', error);
    return { data: null, error };
  }
}

/**
 * Delete admin roadmap and all associated days
 */
/**
 * Publish or unpublish an admin roadmap
 */
export async function publishAdminRoadmap(
  roadmapId: string,
  isPublished: boolean
): Promise<{ data: any | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('admin_roadmaps')
      .update({ is_published: isPublished })
      .eq('id', roadmapId)
      .select();

    if (error) {
      console.error('Error publishing roadmap:', error);
      return { data: null, error };
    }

    return { data: data?.[0], error: null };
  } catch (error) {
    console.error('Error in publishAdminRoadmap:', error);
    return { data: null, error };
  }
}

/**
 * Delete an admin roadmap
 */
export async function deleteAdminRoadmap(
  roadmapId: string
): Promise<{ error: any }> {
  try {
    // Days will cascade delete due to FK constraint
    const { error } = await supabase
      .from('admin_roadmaps')
      .delete()
      .eq('id', roadmapId);

    if (error) {
      console.error('Error deleting roadmap:', error);
      return { error };
    }

    return { error: null };
  } catch (error) {
    console.error('Error in deleteAdminRoadmap:', error);
    return { error };
  }
}

/**
 * Get user's progress on admin roadmap
 */
export async function getUserProgressOnAdminRoadmap(
  userId: string,
  roadmapId: string
): Promise<{ data: any | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('user_admin_roadmap_progress')
      .select('*')
      .eq('user_id', userId)
      .eq('admin_roadmap_id', roadmapId)
      .single();

    if (error && error.code !== 'PGRST116') {
      // PGRST116 means no rows found, which is fine
      console.error('Error fetching progress:', error);
      return { data: null, error };
    }

    return { data: data || null, error: null };
  } catch (error) {
    console.error('Error in getUserProgressOnAdminRoadmap:', error);
    return { data: null, error };
  }
}

/**
 * Update user progress on admin roadmap
 */
export async function updateUserProgressOnAdminRoadmap(
  userId: string,
  roadmapId: string,
  completedDay: number,
  totalDays: number
): Promise<{ data: any | null; error: any }> {
  try {
    // Get current progress
    const { data: existing } = await supabase
      .from('user_admin_roadmap_progress')
      .select('*')
      .eq('user_id', userId)
      .eq('admin_roadmap_id', roadmapId)
      .single();

    let completedDays = existing?.completed_days || [];
    if (!completedDays.includes(completedDay)) {
      completedDays = [...completedDays, completedDay];
    }

    const progressPercentage = Math.round((completedDays.length / totalDays) * 100);
    const isCompleted = completedDays.length === totalDays;

    if (existing) {
      // Update existing progress
      const { data, error } = await supabase
        .from('user_admin_roadmap_progress')
        .update({
          current_day: Math.max(...completedDays) + 1,
          completed_days: completedDays,
          progress_percentage: progressPercentage,
          completed_at: isCompleted ? new Date().toISOString() : null,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId)
        .eq('admin_roadmap_id', roadmapId)
        .select()
        .single();

      return { data, error };
    } else {
      // Create new progress record
      const { data, error } = await supabase
        .from('user_admin_roadmap_progress')
        .insert({
          user_id: userId,
          admin_roadmap_id: roadmapId,
          current_day: completedDay + 1,
          completed_days: completedDays,
          progress_percentage: progressPercentage,
          completed_at: isCompleted ? new Date().toISOString() : null,
        })
        .select()
        .single();

      return { data, error };
    }
  } catch (error) {
    console.error('Error in updateUserProgressOnAdminRoadmap:', error);
    return { data: null, error };
  }
}

/**
 * Increment view count for admin roadmap
 */
export async function incrementAdminRoadmapViewCount(
  roadmapId: string
): Promise<{ error: any }> {
  try {
    const { error } = await supabase
      .from('admin_roadmaps')
      .update({
        view_count: supabase.rpc('increment', {
          x: 1,
          table_name: 'admin_roadmaps',
          row_id: roadmapId,
        }),
      })
      .eq('id', roadmapId);

    return { error };
  } catch (error) {
    console.error('Error incrementing view count:', error);
    return { error };
  }
}
